import {
  VariantSwitcher,
  type VariantSwitcherSegment,
} from "@faire/prototype-playground/components/VariantSwitcher";
import { useMemo, type ReactNode } from "react";

import { RetailerReportIssueFlow } from "./retailer/RetailerReportIssueFlow";
import {
  RETAILER_SCREEN_PREPACK_AFFECTED,
  RETAILER_SCREEN_RECEIVED,
  RETAILER_SCREEN_RESOLUTION,
  RETAILER_SCREEN_REVIEW,
  RETAILER_SCREEN_SELECT_PRODUCTS,
  SURFACE_BRAND_PORTAL,
  SURFACE_BRAND_PORTAL_ACCOUNT_SETTINGS,
  SURFACE_PRE_PACK_POLICY,
  SURFACE_RETAILER,
  SURFACE_RETAILER_RETURN_REQUEST,
  usePrototypeVariants,
} from "./usePrototypeVariants";

type PrototypeVariantLayoutProps = {
  children: ReactNode;
};

export const PrototypeVariantLayout = ({
  children,
}: PrototypeVariantLayoutProps) => {
  const {
    surface,
    retailerScreen,
    prePackAllItemsAffected,
    isReturnsRequired,
    setSurface,
    setRetailerScreen,
    setPrePackAllItemsAffected,
    setReturnsRequired,
    continueFromPrePackAffected,
    isRetailer,
    isPrePackPolicy,
    isReturnRequest,
    isBrandPortalIssues,
  } = usePrototypeVariants();

  const segments = useMemo(() => {
    const nextSegments: VariantSwitcherSegment[] = [
      {
        featureTitle: "Surface",
        concepts: [
          { variantNumber: SURFACE_BRAND_PORTAL, label: "Brand portal" },
          {
            variantNumber: SURFACE_RETAILER,
            label: "Retailer reports issue",
          },
          {
            variantNumber: SURFACE_PRE_PACK_POLICY,
            label: "Pre-pack policy",
          },
          {
            variantNumber: SURFACE_BRAND_PORTAL_ACCOUNT_SETTINGS,
            label: "Account settings",
          },
          {
            variantNumber: SURFACE_RETAILER_RETURN_REQUEST,
            label: "Retailer return request",
          },
        ],
        activeVariant: surface,
        onVariantChange: setSurface,
        variantParamName: "surface",
      },
    ];

    if (isRetailer) {
      nextSegments.push({
        featureTitle: "Screen",
        concepts: isPrePackPolicy
          ? [
              {
                variantNumber: RETAILER_SCREEN_PREPACK_AFFECTED,
                label: "What was affected",
              },
              {
                variantNumber: RETAILER_SCREEN_RESOLUTION,
                label: "Resolution",
              },
              {
                variantNumber: RETAILER_SCREEN_REVIEW,
                label: "Review & confirm",
              },
              {
                variantNumber: RETAILER_SCREEN_RECEIVED,
                label: "Report received",
              },
            ]
          : isReturnRequest
            ? [
                {
                  variantNumber: RETAILER_SCREEN_SELECT_PRODUCTS,
                  label: "Select products",
                },
                {
                  variantNumber: RETAILER_SCREEN_RESOLUTION,
                  label: "Resolution",
                },
                {
                  variantNumber: RETAILER_SCREEN_REVIEW,
                  label: "Review & confirm",
                },
                {
                  variantNumber: RETAILER_SCREEN_RECEIVED,
                  label: "Report received",
                },
              ]
            : [
                {
                  variantNumber: RETAILER_SCREEN_RESOLUTION,
                  label: "Resolution",
                },
                {
                  variantNumber: RETAILER_SCREEN_REVIEW,
                  label: "Review & confirm",
                },
                {
                  variantNumber: RETAILER_SCREEN_RECEIVED,
                  label: "Report received",
                },
              ],
        activeVariant: retailerScreen,
        onVariantChange: setRetailerScreen,
        variantParamName: "retailerScreen",
      });
    }

    if (isPrePackPolicy) {
      nextSegments.push({
        featureTitle: "Affected scope",
        concepts: [
          { variantNumber: 1, label: "All items" },
          { variantNumber: 0, label: "Some items only" },
        ],
        activeVariant: prePackAllItemsAffected === "yes" ? 1 : 0,
        onVariantChange: (variant: number) =>
          setPrePackAllItemsAffected(variant === 1 ? "yes" : "no"),
        syncVariantToUrl: false,
      });
    }

    if (isBrandPortalIssues) {
      nextSegments.push({
        featureTitle: "Returns required",
        concepts: [
          { variantNumber: 0, label: "Off" },
          { variantNumber: 1, label: "On" },
        ],
        activeVariant: isReturnsRequired ? 1 : 0,
        onVariantChange: (variant: number) => setReturnsRequired(variant === 1),
        syncVariantToUrl: false,
      });
    }

    return nextSegments;
  }, [
    isBrandPortalIssues,
    isPrePackPolicy,
    isRetailer,
    isReturnRequest,
    isReturnsRequired,
    prePackAllItemsAffected,
    retailerScreen,
    setPrePackAllItemsAffected,
    setRetailerScreen,
    setReturnsRequired,
    setSurface,
    surface,
  ]);

  return (
    <>
      <VariantSwitcher
        featureTitle="Damaged / Missing reports"
        segments={segments}
      />
      {isRetailer ? (
        <RetailerReportIssueFlow
          isPrePackPolicy={isPrePackPolicy}
          isReturnRequest={isReturnRequest}
          prePackAllItemsAffected={prePackAllItemsAffected}
          screen={retailerScreen}
          onPrePackAffectedContinue={continueFromPrePackAffected}
          onScreenChange={setRetailerScreen}
        />
      ) : (
        children
      )}
    </>
  );
};
